import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api.js';

const DeliveriesPage = ({ panelId }) => {
    const [showFilters, setShowFilters] = useState(false);
    const [deliveries, setDeliveries] = useState([]); // Şimdilik boş veri
    const isManagement = window.location.hostname.startsWith('yonetim');
    // İzleyici rolü yalnızca görüntüleme yapar.
    const isViewer = (JSON.parse(localStorage.getItem('user') || '{}').role) === 'izleyici';

    // Varsayılan olarak BUGÜN — günlük yatırım/çekim/komisyon otomatik hesaplansın
    const _today = new Date().toISOString().split('T')[0];
    const [filters, setFilters] = useState({
        txid: '',
        company: '',
        status: '',
        startDate: _today,
        endDate: _today
    });

    // Modal ve Form State'leri
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        txid: '',
        description: '',
        usdtAmount: '',
        usdtRate: 0,
        tlAmount: '',
        user: '',
        selectedPanelId: ''
    });
    const [panels, setPanels] = useState([]);
    const [investments, setInvestments] = useState([]);
    const [withdrawals, setWithdrawals] = useState([]);
    const [commissionRate, setCommissionRate] = useState(0);
    const [activeTab, setActiveTab] = useState('deliveries'); // 'deliveries' | 'mutabakat'
    const [mutabakatList, setMutabakatList] = useState([]);
    const [panelSummary, setPanelSummary] = useState({ devir: 0, kasa: 0, todayDeposit: 0, todayWithdrawal: 0, todayTeslim: 0, todayKomisyon: 0 });

    const logAction = async (action, details) => {
        try {
            const user = JSON.parse(localStorage.getItem('user'));
            const activePanelId = panelId || user?.panelId;
            const username = user ? user.username : 'Bilinmiyor';
            await api.createLog({ username, action, details, panelId: activePanelId });
        } catch (error) {
            console.error('Loglama hatası:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const newData = { ...prev, [name]: value };
            // USDT Miktarı değişince TL Miktarını hesapla
            if (name === 'usdtAmount') {
                const amount = parseFloat(value);
                if (!isNaN(amount)) {
                    newData.tlAmount = parseFloat((amount * prev.usdtRate).toFixed(2));
                } else {
                    newData.tlAmount = '';
                }
            }
            return newData;
        });
    };

    const handleAddSubmit = async (e) => {
        e.preventDefault();
        try {
            const currentUser = JSON.parse(localStorage.getItem('user'));
            const activePanelId = panelId || currentUser?.panelId;

            const finalPanelId = (isManagement && formData.selectedPanelId) ? formData.selectedPanelId : activePanelId;

            await api.createDelivery({
                ...formData,
                status: 'Bekliyor',
                company: '-',
                panelId: finalPanelId
            });
            logAction('Teslimat Ekleme', `TXID: ${formData.txid} eklendi.`);
            fetchDeliveries(); // Listeyi yenile
            setShowModal(false);
            setFormData({
                txid: '',
                description: '',
                usdtAmount: '',
                usdtRate: 0,
                tlAmount: '',
                user: '',
                selectedPanelId: ''
            });
        } catch (error) {
            alert('Kayıt sırasında hata oluştu.');
        }
    };

    const fetchDeliveries = useCallback(async () => {
        try {
            const currentUser = JSON.parse(localStorage.getItem('user'));
            const activePanelId = panelId || currentUser?.panelId;
            const res = await api.getDeliveries({ panelId: activePanelId || '' });
            setDeliveries(res.data);
        } catch (error) {
            console.error('Teslimatlar çekilemedi:', error);
        }
    }, [panelId]);

    const fetchUsdtRate = async () => {
        try {
            const res = await api.getUsdtRate();
            if (res.data && res.data.price) {
                setFormData(prev => ({ ...prev, usdtRate: parseFloat(res.data.price) }));
            }
        } catch (error) {
            console.error('Kur çekilemedi:', error);
        }
    };

    const fetchPanels = async () => {
        try {
            const res = await api.getPanels();
            setPanels(res.data);
        } catch (error) {
            console.error('Paneller çekilemedi:', error);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const filteredDeliveries = useMemo(() => {
        return deliveries.filter(item => {
            const itemDate = new Date(item.createdAt);
            const start = filters.startDate ? new Date(filters.startDate) : null;
            const end = filters.endDate ? new Date(filters.endDate) : null;
            if (start) start.setHours(0, 0, 0, 0);
            if (end) end.setHours(23, 59, 59, 999);

            const matchesTxid = !filters.txid || (item.txid && item.txid.toLowerCase().includes(filters.txid.toLowerCase()));
            const matchesCompany = !filters.company || (item.company && item.company.toLowerCase().includes(filters.company.toLowerCase()));
            const matchesStatus = !filters.status || item.status === filters.status;
            const matchesDate = (!start || itemDate >= start) && (!end || itemDate <= end);
            return matchesTxid && matchesCompany && matchesStatus && matchesDate;
        });
    }, [deliveries, filters]);

    const fetchStats = useCallback(async () => {
        try {
            const currentUser = JSON.parse(localStorage.getItem('user'));
            const activePanelId = panelId || currentUser?.panelId;
            const [invRes, withRes, usersRes, panelsRes] = await Promise.all([
                api.getInvestments({ status: 'approved', panelId: activePanelId || '' }),
                api.getWithdrawals({ status: 'approved', panelId: activePanelId || '' }),
                api.getUsers({ panelId: activePanelId || '' }),
                api.getPanels()
            ]);

            setInvestments(invRes.data);
            setWithdrawals(withRes.data);

            let rate = 0;
            const currentPanel = (panelsRes.data || []).find(p => p.panelId === activePanelId);
            if (currentPanel) {
                if (currentPanel.commissionRate !== undefined && currentPanel.commissionRate !== null) {
                    rate = parseFloat(currentPanel.commissionRate) || 0;
                }
                setPanelSummary({
                    devir: Number(currentPanel.devir) || 0,
                    kasa: Number(currentPanel.kasa) || 0,
                    todayDeposit: Number(currentPanel.todayDeposit) || 0,
                    todayWithdrawal: Number(currentPanel.todayWithdrawal) || 0,
                    todayTeslim: Number(currentPanel.todayTeslim) || 0,
                    todayKomisyon: Number(currentPanel.todayKomisyon) || 0
                });
            } else if (currentUser && currentUser.commissionRate) {
                rate = parseFloat(currentUser.commissionRate) || 0;
            } else {
                const panelAdmin = (usersRes.data || []).find(u => u.role === 'admin');
                rate = panelAdmin ? parseFloat(panelAdmin.commissionRate || 0) : 0;
            }
            setCommissionRate(rate);
        } catch (error) {
            console.error('İstatistikler alınamadı:', error);
        }
    }, [panelId]);

    const fetchMutabakat = useCallback(async () => {
        try {
            const currentUser = JSON.parse(localStorage.getItem('user'));
            const activePanelId = panelId || currentUser?.panelId;
            if (!activePanelId) return;
            const res = await api.getMutabakat(activePanelId);
            setMutabakatList(res.data || []);
        } catch (error) {
            console.error('Mutabakat geçmişi alınamadı:', error);
        }
    }, [panelId]);

    const stats = useMemo(() => {
        const start = filters.startDate ? new Date(filters.startDate) : null;
        const end = filters.endDate ? new Date(filters.endDate) : null;
        if (start) start.setHours(0, 0, 0, 0);
        if (end) end.setHours(23, 59, 59, 999);

        const rate = Number(commissionRate) || 0;

        let devir = 0;
        let totalInvestment = 0;
        let totalWithdrawal = 0;
        let totalDelivery = 0;
        let totalCommission = 0;
        let kasa = 0;

        if (!filters.startDate && !filters.endDate) {
            // Tarih filtresi yoksa (canlı bugünkü görünüm):
            // Devir doğrudan dün 23:59/00:00 kesinleşmiş dünün kasasından O(1) hızında gelir.
            devir = Number(panelSummary.devir) || 0;

            const TR_OFFSET_MS = 3 * 60 * 60 * 1000;
            const nowTR = new Date(Date.now() + TR_OFFSET_MS);
            const startTodayUTC = Date.UTC(nowTR.getUTCFullYear(), nowTR.getUTCMonth(), nowTR.getUTCDate(), 0, 0, 0) - TR_OFFSET_MS;
            const cutoff = new Date(startTodayUTC);

            // Sadece bugünün işlemlerini topla (O(bugün))
            const todayInvestments = investments.filter(i => new Date(i.createdAt) >= cutoff);
            const todayWithdrawals = withdrawals.filter(w => new Date(w.createdAt) >= cutoff);
            const todayDeliveries = deliveries.filter(d => d.status !== 'Reddedildi' && new Date(d.createdAt) >= cutoff);

            totalInvestment = todayInvestments.reduce((acc, curr) => acc + (curr.amount || 0), 0);
            totalWithdrawal = todayWithdrawals.reduce((acc, curr) => acc + (curr.amount || 0), 0);
            totalDelivery = todayDeliveries.reduce((acc, curr) => acc + (parseFloat(curr.tlAmount) || 0), 0);
            totalCommission = totalInvestment * rate / 100;
            kasa = devir + totalInvestment - totalWithdrawal - totalDelivery - totalCommission;
        } else {
            // Belirli bir tarih aralığı filtrelendiyse:
            const cutoff = start;
            if (cutoff) {
                const prevInvestments = investments.filter(i => new Date(i.createdAt) < cutoff);
                const prevWithdrawals = withdrawals.filter(w => new Date(w.createdAt) < cutoff);
                const prevDeliveries = deliveries.filter(d => d.status !== 'Reddedildi' && new Date(d.createdAt) < cutoff);

                const prevInvSum = prevInvestments.reduce((acc, curr) => acc + (curr.amount || 0), 0);
                const prevWdSum = prevWithdrawals.reduce((acc, curr) => acc + (curr.amount || 0), 0);
                const prevDelSum = prevDeliveries.reduce((acc, curr) => acc + (parseFloat(curr.tlAmount) || 0), 0);
                const prevKomSum = prevInvSum * rate / 100;
                devir = prevInvSum - prevWdSum - prevDelSum - prevKomSum;
            }

            const periodInvestments = investments.filter(i => {
                const d = new Date(i.createdAt);
                return (!cutoff || d >= cutoff) && (!end || d <= end);
            });
            const periodWithdrawals = withdrawals.filter(w => {
                const d = new Date(w.createdAt);
                return (!cutoff || d >= cutoff) && (!end || d <= end);
            });
            const periodDeliveries = deliveries.filter(d => {
                if (d.status === 'Reddedildi') return false;
                const dt = new Date(d.createdAt);
                return (!cutoff || dt >= cutoff) && (!end || dt <= end);
            });

            totalInvestment = periodInvestments.reduce((acc, curr) => acc + (curr.amount || 0), 0);
            totalWithdrawal = periodWithdrawals.reduce((acc, curr) => acc + (curr.amount || 0), 0);
            totalDelivery = periodDeliveries.reduce((acc, curr) => acc + (parseFloat(curr.tlAmount) || 0), 0);
            totalCommission = totalInvestment * rate / 100;
            kasa = devir + totalInvestment - totalWithdrawal - totalDelivery - totalCommission;
        }

        return { devir, totalInvestment, totalWithdrawal, totalDelivery, totalCommission, kasa };
    }, [investments, withdrawals, deliveries, filters.startDate, filters.endDate, commissionRate, panelSummary]);

    useEffect(() => {
        document.title = 'PayzoPay - Teslimler';
        fetchStats();
        fetchDeliveries();
        fetchMutabakat();
        const interval = setInterval(() => { fetchStats(); fetchDeliveries(); fetchMutabakat(); }, 5000);
        return () => clearInterval(interval);
    }, [fetchStats, fetchDeliveries, fetchMutabakat]);

    const cardContainerStyle = {
        display: 'flex',
        gap: '20px',
        marginBottom: '20px',
        flexWrap: 'wrap'
    };

    const cardStyle = (borderColor) => ({
        backgroundColor: 'var(--card-bg)',
        borderRadius: '8px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        padding: '20px',
        borderLeft: `5px solid ${borderColor}`,
        flex: 1,
        minWidth: '200px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
    });

    const titleStyle = {
        margin: '0 0 10px 0',
        color: '#6c757d',
        fontSize: '0.9rem',
        fontWeight: 'bold',
        textTransform: 'uppercase'
    };

    const amountStyle = {
        margin: 0,
        fontSize: '1.8rem',
        fontWeight: 'bold',
        color: 'var(--text-color)'
    };

    const labelStyle = { fontWeight: 'bold', fontSize: '0.9em', marginBottom: '5px', display: 'block', color: 'var(--text-color)' };
    const inputStyle = { width: '100%', padding: '8px', border: '1px solid var(--input-border)', borderRadius: '4px', boxSizing: 'border-box', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)' };

    return (
        <div style={{ padding: '20px' }}>
            <h2 style={{ color: '#fff', marginBottom: '20px' }}>
                Teslimler
            </h2>

            <div style={cardContainerStyle}>
                {/* Devir - MOR */}
                <div style={cardStyle('#6f42c1')}>
                    <h5 style={titleStyle}>Devir {filters.startDate ? '(Önceki Kasa)' : '(Dünden Kalan)'}</h5>
                    <p style={amountStyle}>
                        {(stats.devir || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                </div>

                {/* Toplam Yatırım - YEŞİL */}
                <div style={cardStyle('#28a745')}>
                    <h5 style={titleStyle}>Toplam Yatırım</h5>
                    <p style={amountStyle}>
                        {(stats.totalInvestment || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                </div>

                {/* Toplam Çekim - KIRMIZI */}
                <div style={cardStyle('#dc3545')}>
                    <h5 style={titleStyle}>Toplam Çekim</h5>
                    <p style={amountStyle}>
                        {(stats.totalWithdrawal || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                </div>

                {/* Toplam Teslim - SARI */}
                <div style={cardStyle('#ffc107')}>
                    <h5 style={titleStyle}>Toplam Teslim</h5>
                    <p style={amountStyle}>
                        {(stats.totalDelivery || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                </div>

                {/* Toplam Komisyon - MAVİ */}
                <div style={cardStyle('#007bff')}>
                    <h5 style={titleStyle}>Toplam Komisyon {commissionRate > 0 ? `(%${commissionRate})` : ''}</h5>
                    <p style={amountStyle}>
                        {(stats.totalCommission || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                </div>

                {/* Kasa (Kalan) - TURKUAZ */}
                <div style={cardStyle('#20c997')}>
                    <h5 style={titleStyle}>Kasa (Kalan)</h5>
                    <p style={{ ...amountStyle, color: (stats.kasa || 0) >= 0 ? '#20c997' : '#dc3545' }}>
                        {(stats.kasa || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                </div>
            </div>

            {/* Sekmeler: Teslimat Listesi / Günlük Mutabakat */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                <button
                    onClick={() => setActiveTab('deliveries')}
                    style={{
                        padding: '10px 20px',
                        borderRadius: '8px',
                        border: 'none',
                        backgroundColor: activeTab === 'deliveries' ? '#007bff' : 'var(--card-bg)',
                        color: activeTab === 'deliveries' ? '#fff' : 'var(--text-color)',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    <i className="fas fa-box-open"></i>
                    Teslimat Listesi
                </button>
                <button
                    onClick={() => { setActiveTab('mutabakat'); fetchMutabakat(); }}
                    style={{
                        padding: '10px 20px',
                        borderRadius: '8px',
                        border: 'none',
                        backgroundColor: activeTab === 'mutabakat' ? '#6f42c1' : 'var(--card-bg)',
                        color: activeTab === 'mutabakat' ? '#fff' : 'var(--text-color)',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    <i className="fas fa-calendar-alt"></i>
                    Günlük Mutabakat
                </button>
            </div>

            {activeTab === 'mutabakat' && (
                <div style={{ backgroundColor: 'var(--card-bg)', borderRadius: '15px', boxShadow: 'var(--shadow)', overflow: 'hidden', padding: '20px', marginBottom: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                        <h3 style={{ margin: 0, color: '#fff' }}>Günlük Mutabakat Tablosu</h3>
                        <button
                            onClick={fetchMutabakat}
                            style={{ padding: '6px 14px', borderRadius: '6px', border: 'none', backgroundColor: '#6f42c1', color: '#fff', cursor: 'pointer', fontWeight: 'bold' }}
                        >
                            <i className="fas fa-sync" style={{ marginRight: '6px' }}></i> Yenile
                        </button>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                        <table cellPadding="12" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 8px', color: 'var(--text-color)', textAlign: 'right' }}>
                            <thead>
                                <tr style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: '#aaa', fontSize: '0.9em' }}>
                                    <th style={{ textAlign: 'left', padding: '12px' }}>Tarih</th>
                                    <th style={{ padding: '12px', color: '#a78bfa' }}>Devir</th>
                                    <th style={{ padding: '12px', color: '#4ade80' }}>Yatırım</th>
                                    <th style={{ padding: '12px', color: '#f87171' }}>Çekim</th>
                                    <th style={{ padding: '12px', color: '#facc15' }}>Teslim</th>
                                    <th style={{ padding: '12px', color: '#60a5fa' }}>Komisyon (%{commissionRate})</th>
                                    <th style={{ padding: '12px', color: '#2dd4bf', fontWeight: 'bold' }}>Kasa</th>
                                </tr>
                            </thead>
                            <tbody>
                                {mutabakatList.length === 0 ? (
                                    <tr><td colSpan="7" style={{ textAlign: 'center', padding: '20px', opacity: 0.6 }}>Kayıt bulunamadı.</td></tr>
                                ) : (
                                    mutabakatList.map(row => (
                                        <tr key={row.dayKey} style={{ backgroundColor: 'var(--input-bg)' }}>
                                            <td style={{ textAlign: 'left', padding: '12px', fontWeight: 'bold' }}>{row.date}</td>
                                            <td style={{ padding: '12px', color: '#a78bfa' }}>{row.devir.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                            <td style={{ padding: '12px', color: '#4ade80' }}>{row.yatirim.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                            <td style={{ padding: '12px', color: '#f87171' }}>{row.cekim.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                            <td style={{ padding: '12px', color: '#facc15' }}>{row.teslim.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                            <td style={{ padding: '12px', color: '#60a5fa' }}>{row.komisyon.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                            <td style={{ padding: '12px', fontWeight: 'bold', color: row.kasa >= 0 ? '#2dd4bf' : '#f87171' }}>
                                                {row.kasa.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    <p style={{ fontSize: '0.8em', opacity: 0.6, marginTop: '12px', marginBottom: 0 }}>
                        Formül: Kasa = Devir + Yatırım − Çekim − Teslim − Komisyon. Her gün saat 00:00'da kalan kasa, bir sonraki güne devir olarak aktarılır.
                    </p>
                </div>
            )}

            {activeTab === 'deliveries' && (
                <>
                {/* Tablo ve Filtre Alanı */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h3 style={{ margin: 0, color: '#fff' }}>Teslimat Listesi</h3>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            style={{
                                padding: '8px 16px',
                                borderRadius: '20px',
                                border: 'none',
                                backgroundColor: '#007bff',
                                color: '#fff',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                            }}
                        >
                            {showFilters ? 'Filtreleri Gizle' : 'Filtreleri Göster'}
                        </button>
                        {!isViewer && (
                        <button
                            onClick={() => {
                                setShowModal(true);
                                fetchUsdtRate();
                                if (isManagement) fetchPanels();
                            }}
                            style={{
                                padding: '8px 16px',
                                borderRadius: '20px',
                                border: 'none',
                                backgroundColor: '#28a745',
                                color: '#fff',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                            }}
                        >
                            + Ekle
                        </button>
                        )}
                    </div>
                </div>

                <div style={{ backgroundColor: 'var(--card-bg)', borderRadius: '15px', boxShadow: 'var(--shadow)', overflow: 'hidden' }}>
                    {showFilters && (
                        <div style={{ padding: '20px', borderBottom: '1px solid var(--border-color)', backgroundColor: 'var(--table-header-bg)', transition: 'all 0.3s ease' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px' }}>
                                <div>
                                    <label style={labelStyle}>TXID</label>
                                    <input type="text" name="txid" value={filters.txid} onChange={handleFilterChange} style={inputStyle} />
                                </div>
                                <div>
                                    <label style={labelStyle}>Firma</label>
                                    <input type="text" name="company" value={filters.company} onChange={handleFilterChange} style={inputStyle} />
                                </div>
                                <div>
                                    <label style={labelStyle}>Durum</label>
                                    <select name="status" value={filters.status} onChange={handleFilterChange} style={inputStyle}>
                                        <option value="">Hepsi</option>
                                        <option value="Tamamlandı">Tamamlandı</option>
                                        <option value="Bekliyor">Bekliyor</option>
                                    </select>
                                </div>
                                <div>
                                    <label style={labelStyle}>Başlangıç Tarihi</label>
                                    <input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} style={inputStyle} />
                                </div>
                                <div>
                                    <label style={labelStyle}>Bitiş Tarihi</label>
                                    <input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} style={inputStyle} />
                                </div>
                            </div>
                        </div>
                    )}

                    <div style={{ padding: '20px' }}>
                        <table cellPadding="15" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 10px', color: 'var(--text-color)' }}>
                            <thead>
                                <tr style={{ backgroundColor: 'var(--table-header-bg)', borderBottom: '2px solid var(--border-color)', textAlign: 'left' }}>
                                    <th>ID</th>
                                    <th>TXID</th>
                                    <th>Açıklama</th>
                                    <th>Kur(USDT/TL)</th>
                                    <th>Miktar(USDT)</th>
                                    <th>Miktar(TL)</th>
                                    <th>Durum</th>
                                    <th>Kullanıcı</th>
                                    <th>Eklenme Zamanı</th>
                                    <th>Firma</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredDeliveries.length === 0 ? (
                                    <tr><td colSpan="10" style={{ textAlign: 'center' }}>Veri bulunamadı.</td></tr>
                                ) : (
                                    filteredDeliveries.map(item => (
                                        <tr key={item._id}>
                                            <td>{item._id}</td>
                                            <td>{item.txid}</td>
                                            <td>{item.description}</td>
                                            <td>{item.usdtRate}</td>
                                            <td>{Number(item.usdtAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                            <td>{Number(item.tlAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                            <td>{item.status}</td>
                                            <td>{item.user}</td>
                                            <td>{new Date(item.createdAt).toLocaleString()}</td>
                                            <td>{item.company}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                </>
            )}

            {/* Ekleme Modalı */}
            {showModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
                }}>
                    <div style={{ background: 'var(--card-bg)', padding: '20px', borderRadius: '8px', width: '90%', maxWidth: '500px', color: 'var(--text-color)' }}>
                        <h3>Yeni Teslimat Ekle</h3>
                        <form onSubmit={handleAddSubmit}>
                            <div style={{ marginBottom: '10px' }}>
                                <label style={labelStyle}>TXID</label>
                                <input name="txid" value={formData.txid} onChange={handleInputChange} required style={inputStyle} />
                            </div>
                            <div style={{ marginBottom: '10px' }}>
                                <label style={labelStyle}>Açıklama</label>
                                <input name="description" value={formData.description} onChange={handleInputChange} required style={inputStyle} />
                            </div>
                            <div style={{ marginBottom: '10px' }}>
                                <label style={labelStyle}>USDT Miktarı (opsiyonel)</label>
                                <input type="number" name="usdtAmount" value={formData.usdtAmount} onChange={handleInputChange} style={inputStyle} />
                                <div style={{ fontSize: '0.75em', opacity: 0.7, marginTop: '2px' }}>Kur: {formData.usdtRate || 0} — USDT girerseniz TL otomatik hesaplanır.</div>
                            </div>
                            <div style={{ marginBottom: '10px' }}>
                                <label style={labelStyle}>Miktar (TL) — elle ayarlanabilir</label>
                                <input type="number" step="0.01" name="tlAmount" value={formData.tlAmount} onChange={handleInputChange} required style={inputStyle} placeholder="TL tutarını elle girin" />
                            </div>
                            {isManagement && (
                                <div style={{ marginBottom: '10px' }}>
                                    <label style={labelStyle}>Departman</label>
                                    <select name="selectedPanelId" value={formData.selectedPanelId} onChange={handleInputChange} required style={inputStyle}>
                                        <option value="">Seçiniz...</option>
                                        {panels.map(p => (
                                            <option key={p._id} value={p.panelId}>{p.name || p.panelId}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div style={{ marginTop: '20px', textAlign: 'right' }}>
                                <button type="button" onClick={() => setShowModal(false)} style={{ marginRight: '10px', padding: '8px 16px', cursor: 'pointer', background: '#ccc', border: 'none', borderRadius: '4px' }}>İptal</button>
                                <button type="submit" style={{ padding: '8px 16px', background: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Kaydet</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DeliveriesPage;